import { createContext, useContext } from 'react';

import type { HostContext, MfeProps } from '../types';

/**
 * Context for MFE props
 */
export const MfePropsContext = createContext<MfeProps | null>(null);

/**
 * Hook to access MFE props within an MFE
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { hostContext, mfe } = useMfeProps()
 *
 *   if (mfe) {
 *     // Running as MFE
 *     console.log('Enterprise:', hostContext?.enterprise?.name)
 *   }
 *
 *   return <div>...</div>
 * }
 * ```
 */
export function useMfeProps(): MfeProps {
	const props = useContext(MfePropsContext);

	if (!props) {
		// Return default props for standalone mode
		return {
			mfe: false,
			name: 'standalone',
			singleSpa: {} as MfeProps['singleSpa'],
			mountParcel: () => ({
				mount: () => Promise.resolve(null),
				unmount: () => Promise.resolve(null),
				getStatus: () => 'NOT_BOOTSTRAPPED',
				loadPromise: Promise.resolve(null),
				bootstrapPromise: Promise.resolve(null),
				mountPromise: Promise.resolve(null),
				unmountPromise: Promise.resolve(null),
				update: () => Promise.resolve(null),
			}),
		};
	}

	return props;
}

/**
 * Hook to access host context within an MFE
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, user, enterprise } = useHostContext()
 *
 *   return (
 *     <div className={theme === 'dark' ? 'dark' : ''}>
 *       Welcome, {user?.name}!
 *     </div>
 *   )
 * }
 * ```
 */
export function useHostContext(): HostContext {
	const props = useMfeProps();
	return props.hostContext || {};
}

/**
 * Hook to check if running in MFE mode
 */
export function useIsMfe(): boolean {
	const props = useMfeProps();
	return props.mfe === true;
}

/**
 * Hook to get enterprise info from host context
 */
export function useEnterprise() {
	const context = useHostContext();
	return context.enterprise;
}

/**
 * Hook to get user info from host context
 */
export function useUser() {
	const context = useHostContext();
	return context.user;
}

/**
 * Hook to get theme from host context
 */
export function useTheme() {
	const context = useHostContext();
	return context.theme || 'light';
}
